import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Flag from 'react-world-flags';
import { SUPPORTED_LNGS, DEFAULT_LNG } from '../../i18n';

// Language dropdown for the nav. Each option is a real, path-preserving <Link>
// to the same page in that locale (crawlable, reinforces hreflang). The menu is
// always in the DOM (hidden with CSS, not conditionally rendered) so crawlers
// and the prerender see the alternate-locale links even while it's closed.
// Scales to any number of locales — trigger stays a compact flag + code.

// flag = ISO 3166-1 country code for react-world-flags (GB for English —
// GBP pricing, UK-based). Add an entry per locale you support.
const LOCALE_META = {
  en: { label: 'English', short: 'EN', flag: 'GB' },
  es: { label: 'Español', short: 'ES', flag: 'ES' },
};
const metaFor = (lng) => LOCALE_META[lng] || { label: lng, short: lng.toUpperCase(), flag: null };

// "/es/pricing" -> "/pricing" · "/es" -> "/" · "/pricing" -> "/pricing"
function stripLocale(pathname) {
  const seg = pathname.split('/')[1];
  if (SUPPORTED_LNGS.includes(seg) && seg !== DEFAULT_LNG) {
    const rest = pathname.slice(seg.length + 1);
    return rest === '' ? '/' : rest;
  }
  return pathname || '/';
}

function localeHref(basePath, lng) {
  if (lng === DEFAULT_LNG) return basePath;
  return basePath === '/' ? `/${lng}` : `/${lng}${basePath}`;
}

export default function LocaleSwitcher() {
  const { pathname, search, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const seg = pathname.split('/')[1];
  const active = SUPPORTED_LNGS.includes(seg) ? seg : DEFAULT_LNG;
  const base = stripLocale(pathname);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close when the route changes (a selection navigated).
  useEffect(() => { setOpen(false); }, [pathname]);

  if (SUPPORTED_LNGS.length < 2) return null;
  const cur = metaFor(active);

  return (
    <div className={`rs-locale-dd ${open ? 'is-open' : ''}`} ref={ref}>
      <button
        type="button"
        className="rs-locale-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${cur.label}`}
        onClick={() => setOpen((o) => !o)}
      >
        {cur.flag && <Flag code={cur.flag} className="rs-flag" fallback={<span />} />}
        <span className="rs-locale-code">{cur.short}</span>
        <svg className="caret" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <ul className="rs-locale-menu" role="listbox" aria-label="Language">
        {SUPPORTED_LNGS.map((lng) => {
          const m = metaFor(lng);
          const isActive = lng === active;
          return (
            <li key={lng} role="option" aria-selected={isActive}>
              <Link
                className={`rs-locale-item ${isActive ? 'is-active' : ''}`}
                to={`${localeHref(base, lng)}${search}${hash}`}
                hrefLang={lng}
                onClick={() => setOpen(false)}
              >
                {m.flag && <Flag code={m.flag} className="rs-flag" fallback={<span />} />}
                <span className="rs-locale-name">{m.label}</span>
                {isActive && (
                  <svg className="rs-locale-check" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M3 7.5l2.5 2.5L11 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
